import { useState } from 'react';
import {
  useListAdminInvestmentsQuery,
  useListInvestmentPaymentsQuery,
  useListInvestmentPayoutsQuery,
} from '../../features/investments/adminInvestmentsApi';
import { usePlanLookup } from '../../components/investments/usePlanLookup';
import InvestmentCard from '../../components/investments/InvestmentCard';
import Card, { CardHeader } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import EmptyState from '../../components/ui/EmptyState';
import { TrendingUpIcon } from '../../components/ui/icons';
import { SkeletonCardGrid } from '../../components/ui/Skeleton';

// v1.17.0: "Pending payouts only" used to be a separate checkbox alongside
// this dropdown — it's now folded in as its own selectable option instead,
// using a client-only sentinel value ('pending_payout') that never gets
// sent to the server as a status. Selecting it sends has_pending_payout=1
// with no status param; selecting any real status sends status=<value>
// with no has_pending_payout param — the two filters are mutually
// exclusive, never combined, per the backend's documented behavior for
// has_pending_payout as a standalone cross-cutting filter.
const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending_funding', label: 'Pending funding' },
  { value: 'active', label: 'Active' },
  { value: 'matured', label: 'Matured' },
  { value: 'closed', label: 'Closed' },
  { value: 'pending_payout', label: 'Pending payout' },
];

/**
 * GET /admin/investments?status=&per_page=&page=&has_pending_payout= —
 * every investor, gated by manage-investments. The response has no
 * investor info inline (only user_uuid), so the investor column is a
 * truncated uuid — a known display gap per the integration spec, not an
 * invented name.
 */
export default function InvestmentsList() {
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const isPendingPayoutFilter = status === 'pending_payout';
  const { data, isLoading, isFetching, isError } = useListAdminInvestmentsQuery({
    status: isPendingPayoutFilter ? undefined : status || undefined,
    has_pending_payout: isPendingPayoutFilter,
    page,
  });
  const getPlan = usePlanLookup();

  const investments = data?.investments ?? [];
  const meta = data?.meta;
  const parseError = data?.parseError;

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold text-foreground">Investments</h1>
          <Badge tone="success">Live</Badge>
        </div>
        <p className="text-foreground-muted text-sm mt-1">All investments across every investor.</p>
      </div>

      <Card>
        <CardHeader
          title="All investments"
          subtitle={isFetching && !isLoading ? 'Refreshing…' : undefined}
          action={
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
                className="rounded-lg bg-background-elevated border border-border px-3 py-1.5 text-xs text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          }
        />

        {isLoading && <SkeletonCardGrid count={6} />}
        {isError && <p className="py-8 text-center text-danger">Could not load investments. Check your connection and try again.</p>}
        {!isLoading && !isError && parseError && (
          <p className="py-8 text-center text-danger">Something went wrong loading this data. Check the console for details.</p>
        )}
        {!isLoading && !isError && !parseError && investments.length === 0 && (
          <EmptyState icon={TrendingUpIcon} title="No investments yet" body="Investments created for investors will show up here." />
        )}

        {!isLoading && !isError && !parseError && investments.length > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {investments.map((inv) => {
                const plan = getPlan(inv.investmentPlanUuid);
                return (
                  <InvestmentCard
                    key={inv.id}
                    investment={inv}
                    title={plan?.name || 'Unknown plan'}
                    subtitle={inv.userUuid ? `Investor ${inv.userUuid.slice(0, 8)}…` : undefined}
                    detailPath={`/admin/investments/${inv.id}`}
                    usePaymentsQuery={useListInvestmentPaymentsQuery}
                    usePayoutsQuery={useListInvestmentPayoutsQuery}
                  />
                );
              })}
            </div>

            {meta && meta.lastPage > 1 && (
              <div className="flex items-center justify-between pt-4 mt-4 border-t border-border">
                <p className="text-xs text-foreground-subtle">
                  Page {meta.currentPage} of {meta.lastPage} · {meta.total} total
                </p>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" disabled={meta.currentPage <= 1} onClick={() => setPage(Math.max(1, meta.currentPage - 1))}>
                    Previous
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={meta.currentPage >= meta.lastPage}
                    onClick={() => setPage(Math.min(meta.lastPage, meta.currentPage + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
